import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { SvgIcon } from 'components/helpers/SvgIcon';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { ContentGridCard } from '../ContentGrid/ContentGridCard';
import { CardFields, ContentOverride, normalizeStandardImageUrl } from '../CardListingShared';
import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import carouselStyles from './Carousel.module.scss';
import clsx from 'clsx';
import React, { RefObject, useEffect, useRef } from 'react';
import { useCtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
import useDictionary from 'lib/hooks/useDictionary';
import { sendContentResultAnalytics } from 'components/helpers/Analytics/CategoryTilesAnalytics';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { SearchRecord } from 'lib/services/models/search-response';

interface CarouselMainProps {
  headline: React.ReactNode;
  splideRef: RefObject<Splide | null>;
  cards: SearchRecord[];
  totalPages: number;
  activeIndex: number;
  isAtStart: boolean;
  isAtEnd: boolean;
  preambleAlignment: PreambleOrientation;
  cardAlignment?: ContentGridCardAlignment;
  contentOverrides: Record<string, ContentOverride>;
  fallbackImageUrl?: string;
  fieldsToShow: CardFields[];
  gridAspectRatio?: ImageAspectRatio;
  handlePrev: () => void;
  handleNext: () => void;
}

export const CarouselMain = ({
  headline,
  splideRef,
  cards,
  totalPages,
  activeIndex,
  isAtStart,
  isAtEnd,
  preambleAlignment,
  cardAlignment,
  contentOverrides,
  fallbackImageUrl,
  fieldsToShow,
  gridAspectRatio,
  handlePrev,
  handleNext,
}: CarouselMainProps) => {
  const carouselPaginationColor = useCtaComponentClass('component-carousel-pagination-color');
  const { getDictionaryValue } = useDictionary();
  const language = useGlobalData().locale;

  const slideFocusRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardsRef = useRef<SearchRecord[]>(cards);
  const pendingFocusDirectionRef = useRef<'prev' | 'next' | null>(null);
  const pendingFocusKeyboardRef = useRef<boolean>(false);
  const pendingTabFocusIndexRef = useRef<number | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const lastLiveMessageRef = useRef<string>('');
  const announceItemRef = useRef<boolean>(false);
  const pendingAnnounceDirectionRef = useRef<'prev' | 'next' | null>(null);
  const pendingMoveFocusRef = useRef<boolean>(false);

  const announce = (message: string) => {
    if (typeof window === 'undefined') return;

    const liveRegion = liveRegionRef.current;
    if (!liveRegion) return;
    if (!message || message === lastLiveMessageRef.current) return;

    lastLiveMessageRef.current = message;
    liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = message;
      }
    });
  };

  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  const focusSlide = (indexToFocus: number) => {
    const container = slideFocusRefs.current[indexToFocus];
    if (!container) return;

    container.focus();
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId = 0;
    let isBound = false;

    const onMoved = () => {
      const direction = pendingFocusDirectionRef.current;
      pendingFocusDirectionRef.current = null;
      const shouldTabFocus = pendingFocusKeyboardRef.current;
      pendingFocusKeyboardRef.current = false;

      const splide = splideRef.current?.splide;
      if (!splide) return;

      const perPage = Number(splide.options.perPage) || 1;
      const startIndex = splide.index;
      const indexToFocus =
        direction === 'next'
          ? Math.min(cards.length - 1, startIndex + perPage - 1)
          : Math.max(0, startIndex);

      // If the user activated an arrow (mouse or keyboard), move focus to the new card.
      if (pendingMoveFocusRef.current) {
        pendingMoveFocusRef.current = false;
        window.requestAnimationFrame(() => focusSlide(indexToFocus));
      }

      // (Tab from arrow moves into the newly visible card).
      if (shouldTabFocus) {
        pendingTabFocusIndexRef.current = indexToFocus;
      }
    };

    const tryBind = () => {
      const splide = splideRef.current?.splide;
      if (!splide) {
        rafId = window.requestAnimationFrame(tryBind);
        return;
      }

      splide.on('moved.carouselFocus', onMoved);
      isBound = true;
    };

    tryBind();

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      const splide = splideRef.current?.splide;
      if (isBound && splide) {
        splide.off('moved.carouselFocus');
      }
    };
  }, [cards.length, splideRef]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId = 0;
    let isBound = false;

    const onMovedLive = () => {
      if (!announceItemRef.current) return;

      const direction = pendingAnnounceDirectionRef.current;
      announceItemRef.current = false;
      pendingAnnounceDirectionRef.current = null;
      if (!direction) return;

      const splide = splideRef.current?.splide;
      if (!splide) return;

      const currentCards = cardsRef.current;
      const total = currentCards.length;
      if (total <= 0) return;

      const perPage = Number(splide.options.perPage) || 1;
      const startIndex = splide.index;

      // Right arrow: announce the rightmost visible card.
      // Left arrow: announce the leftmost visible card.
      const indexToAnnounce =
        direction === 'next'
          ? Math.min(total - 1, startIndex + perPage - 1)
          : Math.max(0, startIndex);

      const rawTitle = currentCards[indexToAnnounce]?.Title;
      const title = rawTitle ? String(rawTitle).trim() : '';

      const message = title
        ? `Item ${indexToAnnounce + 1} of ${total}. ${title}`
        : `Item ${indexToAnnounce + 1} of ${total}`;

      announce(message);
    };

    const tryBind = () => {
      const splide = splideRef.current?.splide;
      if (!splide) {
        rafId = window.requestAnimationFrame(tryBind);
        return;
      }

      splide.on('moved.carouselLive', onMovedLive);
      isBound = true;
    };

    tryBind();

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      const splide = splideRef.current?.splide;
      if (isBound && splide) {
        splide.off('moved.carouselLive');
      }
    };
  }, [cards.length, splideRef]);

  const onArrowKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    if (pendingTabFocusIndexRef.current === null) return;

    const indexToFocus = pendingTabFocusIndexRef.current;
    pendingTabFocusIndexRef.current = null;

    e.preventDefault();
    window.requestAnimationFrame(() => focusSlide(indexToFocus));
  };

  const onPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    announceItemRef.current = true;
    pendingAnnounceDirectionRef.current = 'prev';
    pendingMoveFocusRef.current = true;
    pendingFocusDirectionRef.current = 'prev';
    // Keyboard-triggered click typically has detail === 0.
    pendingFocusKeyboardRef.current = e.detail === 0;
    handlePrev();
  };

  const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    announceItemRef.current = true;
    pendingAnnounceDirectionRef.current = 'next';
    pendingMoveFocusRef.current = true;
    pendingFocusDirectionRef.current = 'next';
    pendingFocusKeyboardRef.current = e.detail === 0;
    handleNext();
  };

  return (
    <div
      className={clsx(carouselStyles.carouselContainer, {
        [carouselStyles.carouselContainerForSideLeft]: preambleAlignment === 'SideLeft',
      })}
    >
      <div
        ref={liveRegionRef}
        className="screenReaderText"
        aria-live="polite"
        aria-atomic="true"
        role="status"
      />
      <div className={clsx(carouselStyles.carouselWithArrows)}>
        <CtaButton
          onClick={onPrev}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          className={clsx(carouselStyles.carouselPrev, carouselStyles[carouselPaginationColor], {
            [carouselStyles.carouselArrowHidden]: isAtStart,
          })}
          aria-label={getDictionaryValue('Components.Carousel.LeftArrow')}
        >
          <SvgIcon icon="chevron-left" size="sm" viewBox="0 0 14 22" />
        </CtaButton>

        <Splide
          ref={splideRef}
          options={{
            perPage: preambleAlignment === 'SideLeft' ? 3 : 4,
            perMove: 1,
            gap: '1rem',
            pagination: false,
            arrows: false,
            speed: 300,
            rewind: false,
            drag: true,
            breakpoints: {
              767: {
                perPage: 1,
              },
              1199: {
                perPage: preambleAlignment === 'SideLeft' ? 2 : 3,
              },
              1440: {
                perPage: preambleAlignment === 'SideLeft' ? 2 : 3,
              },
            },
            live: false,
          }}
          className={carouselStyles.carouselDiv}
        >
          {cards.map((card, index) => {
            const override = contentOverrides[card.ContentId];
            card.StandardImageUrl = normalizeStandardImageUrl(card.StandardImageUrl, card.PageUrl);
            return (
              <SplideSlide
                key={card.ContentId}
                onClick={() => {
                  sendContentResultAnalytics({
                    content: {
                      ContentId: card.ContentId ?? '',
                      Title: card.Title ?? '',
                    } as Partial<BaseContentResponse>,
                    language,
                    contentType: card.ContentType,
                    recordType: card.RecordType,
                    contentListName:
                      (React.isValidElement(headline) &&
                        (headline.props as { field?: { value?: string } }).field?.value) ||
                      '',
                    templateName: 'carouselList',
                  });
                }}
              >
                <div
                  ref={(el) => {
                    slideFocusRefs.current[index] = el;
                  }}
                  tabIndex={-1}
                  aria-label={card.Title ? String(card.Title) : undefined}
                  className={carouselStyles.focusWrapper}
                >
                  <ContentGridCard
                    card={card}
                    alignment={cardAlignment}
                    contentOverride={override}
                    fallbackImageUrl={fallbackImageUrl}
                    fieldsToShow={fieldsToShow}
                    gridAspectRatio={gridAspectRatio}
                  />
                </div>
              </SplideSlide>
            );
          })}
        </Splide>

        <CtaButton
          onClick={onNext}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          className={clsx(carouselStyles.carouselNext, carouselStyles[carouselPaginationColor], {
            [carouselStyles.carouselArrowHidden]: isAtEnd,
          })}
          aria-label={getDictionaryValue('Components.Carousel.RightArrow')}
        >
          <SvgIcon icon="chevron-right" size="sm" viewBox="0 0 14 22" />
        </CtaButton>
      </div>

      {/* Desktop pagination - below the carousel */}
      <div className={carouselStyles.customPaginationDesktop}>
        {Array.from({ length: totalPages }).map((_, index) => (
          <span
            key={index}
            className={clsx(
              carouselStyles.paginationDot,
              carouselStyles[carouselPaginationColor],
              activeIndex === index && carouselStyles.paginationDotActive,
              carouselStyles[carouselPaginationColor]
            )}
          />
        ))}
      </div>

      {/* Mobile controls with pagination */}
      <div className={carouselStyles.carouselControlsMobile}>
        <CtaButton
          onClick={onPrev}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          ctaVariant="brand"
          className={clsx(
            carouselStyles.carouselPrevMobile,
            carouselStyles[carouselPaginationColor],
            {
              [carouselStyles.carouselArrowHidden]: isAtStart,
            }
          )}
          aria-label={getDictionaryValue('Components.Carousel.LeftArrow')}
        >
          <SvgIcon icon="chevron-left" size="sm" viewBox="0 0 14 22" />
        </CtaButton>

        <div className={carouselStyles.customPaginationMobile}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <span
              key={index}
              className={clsx(
                carouselStyles.paginationDot,
                carouselStyles[carouselPaginationColor],
                activeIndex === index && carouselStyles.paginationDotActive,
                carouselStyles[carouselPaginationColor]
              )}
            />
          ))}
        </div>

        <CtaButton
          onClick={onNext}
          onKeyDown={onArrowKeyDown}
          ctaStyle="none"
          ctaVariant="brand"
          className={clsx(
            carouselStyles.carouselNextMobile,
            carouselStyles[carouselPaginationColor],
            {
              [carouselStyles.carouselArrowHidden]: isAtEnd,
            }
          )}
          aria-label={getDictionaryValue('Components.Carousel.RightArrow')}
        >
          <SvgIcon icon="chevron-right" size="sm" viewBox="0 0 14 22" />
        </CtaButton>
      </div>
    </div>
  );
};
